/**
 * Trial Expired Dialog Component
 * Shows a modern UI when user's free trial has ended
 * Allows user to proceed to payment page to activate subscription
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

/**
 * Trial Expired Dialog Component
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Function to close dialog
 * @param {Object} props.subscriptionData - Subscription data (plan name, amount, etc.)
 */
const TrialExpiredDialog = ({ open, onClose, subscriptionData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleProceedToPayment = () => {
    setLoading(true);
    // Navigate to payment page
    // We'll pass subscription data via state or fetch it on payment page
    navigate('/payment', { 
      state: { 
        subscriptionData: subscriptionData,
        fromLogin: true 
      } 
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Get plan details from subscription data
  const planName = subscriptionData?.planName || 'Your Selected Plan';
  const planCode = subscriptionData?.planCode || '';
  const billingCycle = subscriptionData?.billingCycle || 'monthly';
  const monthlyAmount = subscriptionData?.monthlyAmount || null;
  const yearlyAmount = subscriptionData?.yearlyAmount || null;
  const amount = billingCycle === 'yearly' ? yearlyAmount : monthlyAmount;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }
      }}
    >
      {/* Header */}
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 3,
          py: 2.5,
          backgroundColor: '#1976d2',
          color: 'white',
          borderBottom: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CreditCardIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
            Trial Period Ended
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={loading}
          sx={{ 
            color: 'white',
            '&:hover': { 
              backgroundColor: 'rgba(255,255,255,0.15)',
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Alert Message */}
        <Alert 
          severity="warning" 
          icon={false}
          sx={{ 
            mb: 3,
            borderRadius: 1,
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            '& .MuiAlert-message': {
              width: '100%',
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: '#856404' }}>
            Your free trial has ended
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            To continue using our services, please activate your subscription plan.
          </Typography>
        </Alert>

        {/* Subscription Plan Card */}
        <Card 
          variant="outlined" 
          sx={{ 
            mb: 3,
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            boxShadow: 'none',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Plan Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CheckCircleIcon 
                sx={{ 
                  mr: 1.5, 
                  fontSize: 28,
                  color: '#1976d2',
                }} 
              />
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1976d2',
                  fontSize: '1.5rem',
                }}
              >
                {planName}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2.5, borderColor: '#e0e0e0' }} />
            
            {/* Billing Cycle */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2.5,
                pb: 2,
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#757575',
                  fontWeight: 500,
                }}
              >
                Billing Cycle
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600, 
                  textTransform: 'capitalize',
                  color: '#424242',
                }}
              >
                {billingCycle}
              </Typography>
            </Box>
            
            {/* Pricing */}
            {(monthlyAmount || yearlyAmount) && (
              <Box>
                {monthlyAmount && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#757575',
                        fontWeight: 500,
                      }}
                    >
                      Monthly
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#1976d2',
                        fontSize: '1.25rem',
                      }}
                    >
                      ₹{monthlyAmount.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                )}
                {yearlyAmount && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#757575',
                        fontWeight: 500,
                      }}
                    >
                      Yearly
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#1976d2',
                        fontSize: '1.25rem',
                      }}
                    >
                      ₹{yearlyAmount.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Info Message */}
        <Box sx={{ 
          p: 2.5, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 1,
          border: '1px solid #e0e0e0',
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#616161',
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            Click the button below to proceed with secure payment and activate your subscription.
            Your account will be activated immediately after successful payment.
          </Typography>
        </Box>
      </DialogContent>
      
      {/* Action Buttons */}
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{ 
            minWidth: 120,
            borderColor: '#bdbdbd',
            color: '#616161',
            '&:hover': {
              borderColor: '#9e9e9e',
              backgroundColor: '#f5f5f5',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleProceedToPayment}
          variant="contained"
          disabled={loading}
          endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
          sx={{
            minWidth: 180,
            backgroundColor: '#1976d2',
            color: 'white',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
            },
            '&:disabled': {
              backgroundColor: '#90caf9',
            }
          }}
        >
          {loading ? 'Processing...' : 'Activate Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialExpiredDialog;

